import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { bugReportsApi } from './bug-reports.api';
import type {
  BugReport,
  BugReportFilters,
  BugReportPage,
  BugReportReporter,
  BugReportUpdateInput,
} from '@/types/api';

export const BUG_REPORTS_KEY = ['admin', 'bug-reports'] as const;

export function useBugReports(filters: BugReportFilters, limit = 25, offset = 0) {
  return useQuery<BugReportPage>({
    queryKey: [...BUG_REPORTS_KEY, 'list', filters, limit, offset],
    queryFn: () => bugReportsApi.list(filters, limit, offset),
    staleTime: 10_000,
    placeholderData: keepPreviousData,
  });
}

export function useBugReportReporters() {
  return useQuery<BugReportReporter[]>({
    queryKey: [...BUG_REPORTS_KEY, 'reporters'],
    queryFn: () => bugReportsApi.reporters(),
    staleTime: 60_000,
  });
}

export function useBugReportDetail(id: string | null) {
  return useQuery<BugReport>({
    queryKey: [...BUG_REPORTS_KEY, 'detail', id],
    queryFn: () => bugReportsApi.detail(id as string),
    enabled: !!id,
    staleTime: 5_000,
  });
}

/** Lightweight badge poll - just the unresolvedTotal field. */
export function useBugReportsBadge() {
  return useQuery<BugReportPage>({
    queryKey: [...BUG_REPORTS_KEY, 'badge'],
    queryFn: () => bugReportsApi.list({}, 1, 0),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useUpdateBugReport() {
  const qc = useQueryClient();
  return useMutation<BugReport, Error, { id: string; body: BugReportUpdateInput }>({
    mutationFn: ({ id, body }) => bugReportsApi.update(id, body),
    onSuccess: (data) => {
      // Refresh list, badge, and the specific detail.
      qc.invalidateQueries({ queryKey: [...BUG_REPORTS_KEY, 'list'] });
      qc.invalidateQueries({ queryKey: [...BUG_REPORTS_KEY, 'badge'] });
      qc.setQueryData([...BUG_REPORTS_KEY, 'detail', data.id], data);
    },
  });
}
